<script>
  import Chart from "svelte-chartjs/src/Bar.svelte";
  import { createEventDispatcher, onMount } from "svelte";
  import { DAY, generateCanvasGradient, makeWebsiteRequest, parseNumber, parsePercent, months, monthsShort } from "../Common/tools";
  import {External, M} from "../external";

  export let funnel = null,
    variantIds = [],
    period = null;

  const visitorsColors = ["#FF55B8", "#FF8787"];
  const conversionsColors = ["#3da6ff", "#257cc6"];

  let grouping = "monthly";
  let visitors = 0;
  let conversions = 0;
  let chartDataLabels = [];
  let monthLabels = [];
  let showMonthsBelow = false;
  let chartWrapper = null;

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: false,
      title: false,
      tooltip: {
        mode: "index",
        intersect: false,
        displayColors: false
      }
    },
    scales: {
      xAxes: {
        display: false,
        stacked: true
      },
      yAxes: {
        display: false,
        stacked: true
      }
    },
    layout: {
      padding: 3
    }
  };

  let chartData = {
    labels: [],
    datasets: []
  };

  let chartValues = [];

  let updateGrouping = () => {
    let startTime = period.from || new Date(parseInt(External.createdTime) * 1000);
    let endTime = period.to || new Date();
    startTime.setHours(0, 0, 0, 0);
    endTime.setHours(23, 59, 59, 999);
    let diff = Math.abs(endTime.getTime() - startTime.getTime());
    if (diff <= 31 * DAY) {
      grouping = "daily";
    } else if (diff < 558 * DAY) {
      // 18 months
      grouping = "monthly";
    } else {
      grouping = "yearly";
    }
  };

  export async function reload() {
    updateGrouping();
    let from = External.Tools.dateToISOStr(period && period.from ? period.from : new Date(parseInt(External.createdTime) * 1000));
    let to = External.Tools.dateToISOStr(period && period.to ? period.to : new Date());

    let response = await makeWebsiteRequest({
      action: "getDetailedStats",
      method: "post",
      data: {
        grouping: grouping,
        funnelId: funnel.id,
        from: from,
        to: to,
        variations: JSON.stringify(variantIds)
      }
    });

    if (!response.data) {
      return;
    }
    let stats = response.data || {};

    let summaries = null;
    // join the stats for each variant together
    variantIds.forEach((variantId) => {
      let variantStats = stats[variantId];
      if (summaries == null) {
        chartDataLabels = Object.keys(variantStats);
        summaries = variantStats;
      } else {
        for (let key in variantStats) {
          let value = variantStats[key];
          for (let valueKey in value) {
            if (!isNaN(value[valueKey])) {
              summaries[valueKey] += value[valueKey];
            }
          }
        }
      }
    });

    monthLabels = [];
    showMonthsBelow = chartDataLabels.length > (chartWrapper.offsetWidth > 610 ? 15 : 8);
    chartDataLabels = chartDataLabels.map((label) => {
      let date;
      switch (grouping) {
        case "daily":
          date = new Date(label);
          if (showMonthsBelow) {
            let month = months[date.getMonth()];
            if (monthLabels.length === 0 || monthLabels[monthLabels.length - 1] !== month) {
              monthLabels.push(month);
            }
            return date.getDate();
          } else {
            return External.WebPlatform.dateToStrWithMonth(date, true, false, true, monthsShort);
          }
        case "monthly":
          date = new Date(label);
          return External.WebPlatform.dateToStrWithMonth(date, false, false, false, monthsShort);
        case "yearly":
          return label;
      }
      return label;
    });

    chartValues = Object.values(summaries);
    let visitorsData = chartValues.map((el) => el.sessions);
    let conversionsData = chartValues.map((el) => el.conversions);

    visitors = visitorsData.reduce((a, b) => a + b, 0);
    conversions = conversionsData.reduce((a, b) => a + b, 0);

    let canvas = document.querySelector(".analytics-box-big canvas");
    let visitorsGradient = generateCanvasGradient(canvas, visitorsColors);
    let conversionsGradient = generateCanvasGradient(canvas, conversionsColors);

    chartData = {
      labels: chartDataLabels,
      datasets: [
        {
          label: M.VISITORS_UPPER,
          data: visitorsData,
          backgroundColor: visitorsGradient,
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0.1,
          lineTension: 0.3,
          fill: true,
          borderRadius: 3,
          borderColor: "transparent",
          order: 1
        },
        {
          label: M.CONVERSIONS,
          data: conversionsData,
          backgroundColor: "transparent",
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0.1,
          lineTension: 0.3,
          fill: true,
          borderRadius: 3,
          borderColor: conversionsGradient,
          order: 0,
          type: "line"
        }
      ]
    };
  }

  onMount(() => {
    reload();
  });
</script>

<div class="flex col20 f-left store-section-analytics-warapper narrow-analytics-boxes flex-col-mobile" style="background: rgb(245, 246, 250); margin-top: 0px;">
  <div class="analytics-box analytics-box-small ">
    <h3 class="flex align-center">{M.VISITORS_UPPER} <span class="chart-color ml-10" style="background: #f97f7f" /></h3>
    <h2 class="col20 f-left f-28 gray-text">
      <b>
        {visitors || 0}
      </b>
    </h2>
  </div>
  <div class="analytics-box analytics-box-small ">
    <h3 class="flex align-center">{M.CONVERSIONS} <span class="chart-color ml-10" style="background: #3da6ff" /></h3>
    <h2 class="col20 f-left f-28 gray-text">
      {conversions || 0}
    </h2>
  </div>
</div>
<div class="flex f-left col20 mt-25 flex-col-mobile stats-wrapper">
  <div class="analytics-box-big no-bpadding-mobile col20">
    <div class="col20 f-left relative mt-15 modern-chart-box" bind:this={chartWrapper}>
      <Chart data={chartData} options={chartOptions} />
      <div class="axis">
        {#each chartDataLabels as label, i}
          <div class="tick">
            <span class="tick-label">{label}</span>
            <span class="value value-first">
              <span class="chart-color" style="background: #f97f7f" />
              {chartValues[i].sessions}
            </span>
            <span class="value value-second flex align-center with-color">
              <span class="chart-color" style="background: #3da6ff" />
              {chartValues[i].conversions}
            </span>
          </div>
        {/each}
      </div>
      {#if grouping === "daily" && showMonthsBelow}
        <div class="col20 f-left flex flex-row chart-label-row">
          {#each monthLabels as label, i}
            <span
              class="flex-1"
              class:ta-left={i === 0 && monthLabels.length > 1}
              class:ta-right={i === monthLabels.length - 1 && monthLabels.length > 1}
              class:ta-center={monthLabels.length === 1 || (i > 1 && i < monthLabels.length - 1)}>
              <p>{label}</p>
            </span>
          {/each}
        </div>
      {/if}
    </div>
  </div>
</div>
